import moment from 'moment';
import { useState } from 'react';
import {
  addNewData,
  getAllListData,
  updateData,
} from 'services/CommonServices';
import {
  RetroPayDisassociateResult,
  RetroPayMatchedWeek,
  RetroPayPreview,
  RetroPayQueueRow,
  RetroPayResult,
  RetroPayReview,
  RetroPayState,
  RetroPayTargetProject,
  RetroPayWeek,
} from 'types';
import { MASTER_ENDPOINT, TIMESHEET } from 'utils/Constant';

const staticRetroPay: RetroPayState = {
  disassociateDone: false,
  disassociateResult: null,
  loading: false,
  preview: null,
  queue: null,
  reason: '',
  reasonError: false,
  reasonMode: 'reject',
  rejectDone: false,
  rejectView: false,
  rejecting: false,
  result: null,
  review: null,
  saveError: '',
  saving: false,
  targetProjectConsultantId: '',
  validated: false,
  validating: false,
  visible: false,
};

export const useRetroPay = (options: { onResolved: () => void }) => {
  const { onResolved } = options;
  const [retroPay, setRetroPay] = useState<RetroPayState>(staticRetroPay);

  const loadPreview = (queueId: string, saveError = '') =>
    getAllListData(
      `${MASTER_ENDPOINT.Queue}/${String(queueId)}/retro-pay/preview`,
      true,
    )
      .then((resp: RetroPayPreview) => {
        setRetroPay((prev) => ({
          ...prev,
          loading: false,
          preview: resp,
          rejectView: false,
          saveError,
          targetProjectConsultantId:
            resp?.suggestedTargetProjectConsultantId || '',
        }));
      })
      .catch(() => {
        setRetroPay((prev) => ({ ...prev, loading: false }));
      });

  const openRetroPay = (row: RetroPayQueueRow) => {
    setRetroPay({
      ...staticRetroPay,
      loading: true,
      queue: row,
      visible: true,
    });
    void loadPreview(row.id);
  };

  const retroPayClose = () => setRetroPay(staticRetroPay);

  const retroPayValidate = () => {
    setRetroPay((prev) => ({ ...prev, saveError: '', validating: true }));
    getAllListData(
      `${String(
        TIMESHEET.TimeSheets,
      )}/migrations?sourceProjectConsultantId=${String(
        retroPay?.preview?.source?.projectConsultantId,
      )}&targetProjectConsultantId=${String(
        retroPay.targetProjectConsultantId,
      )}`,
      true,
    )
      .then((resp: RetroPayReview) => {
        setRetroPay((prev) => ({
          ...prev,
          review: resp,
          validated: true,
          validating: false,
        }));
      })
      .catch(() => {
        setRetroPay((prev) => ({ ...prev, validating: false }));
      });
  };

  const retroPaySave = () => {
    setRetroPay((prev) => ({ ...prev, saveError: '', saving: true }));
    addNewData(
      {
        queueId: retroPay?.queue?.id,
        sourceProjectConsultantId:
          retroPay?.preview?.source?.projectConsultantId,
        targetProjectConsultantId: retroPay.targetProjectConsultantId,
      },
      `${String(TIMESHEET.TimeSheets)}/migrations`,
      undefined,
      false,
    )
      .then((resp: RetroPayResult) => {
        setRetroPay((prev) => ({ ...prev, result: resp, saving: false }));
        onResolved();
      })
      .catch((err: unknown) => {
        setRetroPay((prev) => ({
          ...prev,
          review: null,
          saveError:
            typeof err === 'string' && err
              ? err
              : 'Nothing was saved. The item was either already migrated in a different session, or something went wrong during the save. Validate again to retry.',
          saving: false,
          validated: false,
        }));
      });
  };

  const retroPayReject = () => {
    if (!retroPay.reason) {
      setRetroPay((prev) => ({ ...prev, reasonError: true }));
      return;
    }
    setRetroPay((prev) => ({ ...prev, rejecting: true }));
    updateData(
      '',
      { reason: retroPay.reason },
      `${MASTER_ENDPOINT.Queue}/${String(
        retroPay?.queue?.id,
      )}/retro-pay/reject`,
      true,
    )
      .then(() => {
        setRetroPay((prev) => ({
          ...prev,
          rejectDone: true,
          rejecting: false,
        }));
        onResolved();
      })
      .catch(() => {
        setRetroPay((prev) => ({ ...prev, rejecting: false }));
      });
  };

  const retroPayDisassociate = () => {
    if (!retroPay.reason) {
      setRetroPay((prev) => ({ ...prev, reasonError: true }));
      return;
    }
    setRetroPay((prev) => ({ ...prev, rejecting: true }));
    addNewData(
      {
        queueId: retroPay?.queue?.id,
        reason: retroPay.reason,
        sourceProjectConsultantId:
          retroPay?.preview?.source?.projectConsultantId,
      },
      `${String(TIMESHEET.TimeSheets)}/disassociations`,
      undefined,
      false,
    )
      .then((resp: RetroPayDisassociateResult) => {
        setRetroPay((prev) => ({
          ...prev,
          disassociateDone: true,
          disassociateResult: resp,
          rejecting: false,
        }));
        onResolved();
      })
      .catch((err: unknown) => {
        // Nothing was detached. Reload the preview rather than leaving a stale screen: if a
        // destination appeared in the meantime the move flow takes over on its own - the
        // dropdown returns, Validate and Reject come back, and Disassociate drops away.
        setRetroPay((prev) => ({ ...prev, loading: true, rejecting: false }));
        void loadPreview(
          String(retroPay?.queue?.id),
          typeof err === 'string' && err
            ? err
            : 'Nothing was changed. The item has been reloaded.',
        );
      });
  };

  const setReason = (reason: string) =>
    setRetroPay((prev) => ({ ...prev, reason, reasonError: false }));

  const changeTarget = (targetProjectConsultantId: string) =>
    setRetroPay((prev) => ({
      ...prev,
      review: null,
      targetProjectConsultantId,
      validated: false,
    }));

  const openReject = () =>
    setRetroPay((prev) => ({
      ...prev,
      reasonError: false,
      reasonMode: 'reject',
      rejectView: true,
    }));

  const openDisassociate = () =>
    setRetroPay((prev) => ({
      ...prev,
      reasonError: false,
      reasonMode: 'disassociate',
      rejectView: true,
      saveError: '',
    }));

  const cancelReject = () =>
    setRetroPay((prev) => ({ ...prev, reasonError: false, rejectView: false }));

  const retroWeekHours = (ts: RetroPayWeek) =>
    Number(ts.adjustmentHours) > 0
      ? Number(ts.adjustmentHours)
      : Number(ts.hours);

  const retroWeekStatus = (timesheetId: string) => {
    const matched = retroPay?.review?.matchedTimesheets?.find(
      (week: RetroPayMatchedWeek) => week.timesheetId === timesheetId,
    );
    if (matched) {
      return matched.straddlesTargetWindow ? 'split' : 'moves';
    }
    return retroPay?.review?.unmatchedTimesheets?.find(
      (week: RetroPayWeek) => week.timesheetId === timesheetId,
    )
      ? 'outside'
      : '';
  };

  const retroCandidates = retroPay?.preview?.potentialTargetProjects || [];
  const retroSelectedTarget = retroCandidates.find(
    (project: RetroPayTargetProject) =>
      project.projectConsultantId === retroPay.targetProjectConsultantId,
  );
  const retroMatched = retroPay.review?.matchedTimesheets || [];
  const retroStraddles = retroMatched.filter(
    (week: RetroPayMatchedWeek) => week.straddlesTargetWindow,
  );
  const retroCleanMatched = retroMatched.filter(
    (week: RetroPayMatchedWeek) => !week.straddlesTargetWindow,
  );
  const retroCleanHours: number = retroCleanMatched.reduce(
    (sum: number, week: RetroPayMatchedWeek) => sum + retroWeekHours(week),
    0,
  );
  const retroUnmatchedCount = retroPay.review?.unmatchedTimesheets?.length || 0;

  // Disassociate removes only weeks starting after the assignment end. A week straddling that
  // date was partly worked on this assignment and is still paid, so it stays - which is why the
  // count differs from the list above it.
  const retroOrphanedWeeks = retroPay?.preview?.orphanedTimesheets || [];
  const sourceEndDate = retroPay?.preview?.source?.endDate;
  const disassociableCount = sourceEndDate
    ? retroOrphanedWeeks.filter((week: RetroPayWeek) =>
        moment(week.periodFrom).isAfter(moment(sourceEndDate), 'day'),
      ).length
    : 0;
  const straddlingCount = retroOrphanedWeeks.length - disassociableCount;

  return {
    cancelReject,
    changeTarget,
    disassociableCount,
    openDisassociate,
    openReject,
    openRetroPay,
    retroCandidates,
    retroCleanHours,
    retroCleanMatched,
    retroMatched,
    retroPay,
    retroPayClose,
    retroPayDisassociate,
    retroPayReject,
    retroPaySave,
    retroPayValidate,
    retroSelectedTarget,
    retroStraddles,
    retroUnmatchedCount,
    retroWeekHours,
    retroWeekStatus,
    setReason,
    straddlingCount,
  };
};

export type UseRetroPay = ReturnType<typeof useRetroPay>;
